import { ReactComponent as ServiceJobTrackingIcon } from '../assets/icons/building.svg';
import { ReactComponent as EmployeeOnboardingIcon } from '../assets/icons/user-checkmark.svg';
import { ReactComponent as RFITrackingIcon } from '../assets/icons/clipboard.svg';
import { ReactComponent as BidProjectTrackingIcon } from '../assets/icons/hand.svg';
import { ReactComponent as DailyTimesheetsLEMRreportsIcon } from '../assets/icons/bar-chart.svg';
import { ReactComponent as BoardIcon } from '../assets/icons/folder.svg';
import { ReactComponent as FormIcon } from '../assets/icons/document.svg';
import { ReactComponent as CustomFieldIcon } from '../assets/icons/checkmark-box.svg';

// eslint-disable-next-line import/prefer-default-export
export const workflowMap = {
  'Service Job Tracking': {
    shortDescription: 'Track service requests, dispatch, field work, and invoicing in one connected workflow.',
    longDescription: `
    The Service Job Management workflow enables teams to streamline service operations from initial request through job completion and invoicing. Service requests can be submitted internally or externally through a public-facing form, creating and tracking jobs in a centralized board.
Once a request is created, users can convert it into a project, dispatch work using the scheduling system, and track field execution through mobile-enabled work order forms. Field teams can log labor, materials, and equipment usage directly from the job site, with all data syncing back to the board in real time.
As jobs progress, statuses update automatically based on form inputs, ensuring visibility across all active service work. Once completed, invoices can be generated directly from the workflow, moving jobs through to closure.
`,
    icon: ServiceJobTrackingIcon,
    color: '#DBEAFE',
    requirements: [
      {
        title: 'Ontraccr Account',
        value: true,
      },
      {
        title: 'Admin Permissions',
        value: 'isAdmin',
      },
    ],
    automations: [
      'Auto-create service requests from external form submissions',
      'Auto-create project when card moves to “Create Project”',
      'Auto-schedule jobs through the Dispatch Form',
      'Auto-trigger Work Order Form on clock-in',
      'Auto-update board status based on the “Job Complete?” response',
      'Auto-move card to “Invoice Sent” upon invoice submission',
      'Auto-map board card details into linked forms (Dispatch, Work Order, Invoice)',
    ],
    categories: [
      'Service',
      'Field Operations',
      'Scheduling',
      'Tracking',
      'Automation',
      'Forms',
    ],
    categorySet: new Set([
      'Service',
      'Field Operations',
      'Scheduling',
      'Tracking',
      'Automation',
      'Forms',
    ]),
    commonUseCases: [
      'Manage incoming service requests from customers or tenants',
      'Dispatch field teams and track job progress in real time',
      'Capture job details and generate invoices directly from completed work',
    ],
    components: [
      {
        type: 'boards',
        name: 'Boards',
        values: [
          'Service Tracker Board',
        ],
      },
      {
        type: 'forms',
        name: 'Forms',
        values: [
          'Dispatch Form',
          'Work Order Form',
          'Invoice Form',
          'Service Request Form',
        ],
      },
    ],
  },
  'HR - Employee Onboarding': {
    shortDescription: 'Automate employee intake, documents, and onboarding progress in a structured workflow.',
    longDescription: `
    The Employee Onboarding workflow helps HR teams efficiently manage the onboarding process for new hires. It begins with a New Employee Intake Form that automatically creates a user profile and generates a tracking card in the onboarding board.
From there, employees are guided through required forms such as direct deposit and tax documentation, with each step assigned automatically. Progress is tracked visually in the onboarding board, giving HR teams full visibility into each employee’s status.
Once all required forms are completed, a final checklist is assigned to the employee’s manager for review, ensuring a structured and complete onboarding process.
    `,
    icon: EmployeeOnboardingIcon,
    color: '#DCFCE7',
    requirements: [
      {
        title: 'Ontraccr Account',
        value: true,
      },
      {
        title: 'Admin Permissions',
        value: 'isAdmin',
      },
    ],
    automations: [
      'Auto-create employee profile from intake form',
      'Auto-create board card for each new hire',
      'Auto-assign onboarding forms in sequence',
      'Auto-assign manager checklist upon completion',
      'Auto-update board status as steps are completed',
      'Auto-map employee data from intake form into subsequent onboarding forms',
    ],
    categories: [
      'HR',
      'Onboarding',
      'Intake',
      'Forms',
      'Automation',
      'Tracking',
    ],
    categorySet: new Set([
      'HR',
      'Onboarding',
      'Intake',
      'Forms',
      'Automation',
      'Tracking',
    ]),
    commonUseCases: [
      'Onboard new employees with a structured, repeatable process',
      'Ensure all required HR documents are completed before start date',
      'Track onboarding progress across multiple hires simultaneously',
    ],
    components: [
      {
        type: 'boards',
        name: 'Boards',
        values: [
          'Employee Onboarding Board',
        ],
      },
      {
        type: 'forms',
        name: 'Forms',
        values: [
          'New Employee Intake Form',
          'Direct Deposit Form',
          'Tax Documentation Form',
          'Employee Onboarding Checklist Form',
        ],
      },
    ],
  },
  'Project Management: RFI Tracking': {
    shortDescription: 'Track RFIs from submission to resolution with routing and board visibility.',
    longDescription: `
    The RFI Tracking workflow enables teams to capture, manage, and track RFIs across all projects in a centralized system. RFIs are submitted through a standardized form and automatically routed to the appropriate project manager or approver.
Once approved, RFIs are logged in the RFI Tracker Board, where users can monitor progress, assign responsibility, and track status changes. All RFI data is captured and mapped directly to board cards, providing a clear audit trail and real-time visibility.
Teams can filter RFIs by project, priority, or assigned user, ensuring nothing falls through the cracks.
    `,
    icon: RFITrackingIcon,
    color: '#E8D4FE',
    requirements: [
      {
        title: 'Ontraccr Account',
        value: true,
      },
      {
        title: 'Admin Permissions',
        value: 'isAdmin',
      },
    ],
    automations: [
      'Auto-route RFI for approval upon submission',
      'Auto-create board card after approval',
      'Auto-send RFI to external stakeholders via email',
      'Auto-map form data into board card fields',
    ],
    categories: [
      'Project Management',
      'RFI',
      'Communication',
      'Tracking',
      'Approval',
      'Notifications',
    ],
    categorySet: new Set([
      'Project Management',
      'RFI',
      'Communication',
      'Tracking',
      'Approval',
      'Notifications',
    ]),
    commonUseCases: [
      'Manage RFIs across multiple projects in one centralized view',
      'Route RFIs internally before sending to external stakeholders',
      'Track response times and prevent RFIs from being missed or delayed',
    ],
    components: [
      {
        type: 'boards',
        name: 'Boards',
        values: [
          'RFI Tracker Board',
        ],
      },
      {
        type: 'forms',
        name: 'Forms',
        values: [
          'RFI Submission Form',
        ],
      },
      {
        type: 'customFields',
        name: 'Custom Fields',
        values: [
          'Project Manager',
          'Supervisor',
        ],
      },
    ],
  },
  'Bid & Project Tracking': {
    shortDescription: 'Track bids from pipeline through award and into active construction with boards and forms.',
    longDescription: `
      The Bid & Project Tracking workflow helps teams manage opportunities from early bid intake through project handoff. Users can manually create bid requests in the Estimating Pipeline Board to track incoming opportunities, organize bid activity, and maintain visibility across their pipeline.
    When a quote is ready, users can generate a formal proposal using the Bid Submission Form, which includes pricing, terms and conditions, and exclusions. Once submitted, the workflow automatically updates the opportunity status, ensuring accurate tracking of bid activity.
    If a bid is won, the workflow automatically creates a project in Ontraccr and moves the opportunity into the Active Construction Board, providing a seamless transition from estimating into execution.
    `,
    icon: BidProjectTrackingIcon,
    color: '#DCFCE7',
    requirements: [
      {
        title: 'Ontraccr Account',
        value: true,
      },
      {
        title: 'Admin Permissions',
        value: 'isAdmin',
      },
    ],
    automations: [
      'Manual creation and tracking of bid opportunities in the Estimating Pipeline Board',
      'Launch Bid Submission Form directly from the board card',
      'Auto-map board card details into the Bid Submission Form',
      'Auto-move opportunity to “Bid Submitted” upon form submission',
      'Auto-create project when opportunity is moved to “Won”',
      'Auto-move won opportunities into the Active Construction Board',
    ],
    categories: [
      'Project Management',
      'Estimating',
      'CRM',
      'Tracking',
      'Pipeline',
      'Forms',
      'Automation',
    ],
    categorySet: new Set([
      'Project Management',
      'Estimating',
      'CRM',
      'Tracking',
      'Pipeline',
      'Forms',
      'Automation',
    ]),
    commonUseCases: [
      'Track bid opportunities and proposal activity in one central pipeline',
      'Generate formal client proposals with standardized terms and exclusions',
      'Transition won work from estimating into active project tracking automatically',
    ],
    components: [
      {
        type: 'boards',
        name: 'Boards',
        values: [
          'Estimating Pipeline Board',
          'Active Construction Board',
        ],
      },
      {
        type: 'forms',
        name: 'Forms',
        values: [
          'Bid Submission Form',
        ],
      },
    ],
    forms: [
      {
        type: 'forms',
        name: 'Forms',
        values: [
          'Bid Submission Form',
        ],
      },
    ],
  },
  'Daily Timesheets & LEM Reports': {
    shortDescription: 'Capture daily labor, equipment, and materials with flexible timesheets and LEM reports.',
    longDescription: `The Daily Timesheets & LEM Reports workflow provides multiple ways for teams to capture daily job site activity. It includes both team-based and individual timesheet formats, allowing flexibility based on crew structure and reporting preferences.
For companies requiring detailed tracking of labor, equipment, and materials, the workflow includes LEM reports that automatically pull data from timesheets. This ensures accurate and consistent reporting without duplicate data entry.
The included grid view option provides a familiar interface for civil contractors, making it easy to review and approve daily production data.
`,
    icon: DailyTimesheetsLEMRreportsIcon,
    color: '#FFF8C2',
    requirements: [
      {
        title: 'Ontraccr Account',
        value: true,
      },
      {
        title: 'Admin Permissions',
        value: 'isAdmin',
      },
    ],
    automations: [
      'Auto-populate LEM reports from timesheet data',
      'Auto-route submissions to Project Manager or Supervisor for approval',
      'Auto-organize entries by project and date',
    ],
    categories: [
      'Field Operations',
      'Time Tracking',
      'Reporting',
      'Forms',
      'Daily Reports',
    ],
    categorySet: new Set([
      'Field Operations',
      'Time Tracking',
      'Reporting',
      'Forms',
      'Daily Reports',
    ]),
    commonUseCases: [
      'Track daily labor hours across crews or individuals',
      'Capture equipment and material usage for job costing',
      'Generate standardized daily reports for project tracking and billing',
    ],
    components: [
      {
        type: 'forms',
        name: 'Forms',
        values: [
          'Team-Based Daily Timesheet Form',
          'Individual Daily Timesheet Form',
          'Daily LEM Report Form',
        ],
      },
      {
        type: 'customFields',
        name: 'Custom Fields',
        values: [
          'Project Manager',
          'Supervisor',
          'Equipment Used Table',
          'Material Used Table'
        ],
      },
    ],
  },
};

export const componentIcons = {
  boards: {
    icon: <BoardIcon />,
    color: '#E3F2FD',
  },
  forms: {
    icon: <FormIcon />,
    color: '#FCE7F3',
  },
  customFields: {
    icon: <CustomFieldIcon />,
    color: '#F3E8FF',
  },
};
